import {
  Button,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { ChangeEvent, useEffect, useState } from "react";
import CustomerList from "../CustomerList/CustomerList";
import CustomerListCards from "../CustomerListingResponsive/CustomerListingResponsive";
import ImageAndText from "../ImageAndText/ImageAndText";
import { CustomerlistingStyles } from "./CustomerListingStyles";
import HeadingText from "../../common/HeadingText/HeadingText";
import {
  CUSTOMERS,
  CUSTOMER_LISTING_CAPS,
  FILTER,
  SEARCH_ICON,
} from "../../constants";
import { filterPopUpCommonOptions } from "../../constants/FilterPopUpCommonOptions";
import filterIcon from "../../assets/svgs/filter-icon.svg";
import FilterPopUp from "../FilterPopUp/FilterPopUp";
import {
  customerListingData,
  customerListingTableHeadingsData,
} from "../../constants/customerListingData";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { PostCustomerSearch } from "./CustomerListingAction";
import { RootState } from "../../store/store";
import { useTranslation } from "react-i18next";
import { getCookie } from "../../utils/Utility";
import {clearCustomerSearchData} from "./CustomerListingAction"


const CustomerListing: React.FC = () => {
  const isSmallScreen = useMediaQuery("(max-width:1023px)");
  const [selectedKey, setSelectedKey] = useState(
    filterPopUpCommonOptions[0].key
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const carrier = getCookie("carrier");
  const system = getCookie("system");
  const dispatch = useAppDispatch();
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const { customerSerachData } = useAppSelector(
    (state: RootState) => state.customerSearch
  );

  const { isError } = useAppSelector((state: RootState) => state.common);

  const { t } = useTranslation();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (key: string) => {
    setSelectedKey(key);
    handleClose();
  };
  const [searchParams, setSearchParams] = useState<Record<string, string>>({
    customerName: "",
    mobileNumber: "",
    postalCode: "",
  });

  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({
    customerName: "",
    mobileNumber: "",
    postalCode: "",
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrorMessages({});
    setSearchParams({ ...searchParams, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    const newErrors: Record<string, string> = {};
    let hasAnyInput = false;

    Object.entries(searchParams).forEach(([key, value]) => {
      if (value.length > 0) {
        hasAnyInput = true;
        if (!value.trim()) {
          newErrors[key] = "This field cannot contain only spaces";
        }
      }
    });

    if (!hasAnyInput) {
      setErrorMessages({
        customerName: "Fill at least one field before searching.",
      });
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrorMessages(newErrors);
      return;
    }

    setErrorMessages({});
    dispatch(
      PostCustomerSearch({
        ...searchParams,
        customerName: searchParams.customerName.trim(),
        mobileNumber: searchParams.mobileNumber.trim(),
        postalCode: searchParams.postalCode.trim(),
        carrier: carrier,
        system: system,
      })
    );
  };

  useEffect(() => {
    if (isError) {
      setErrorMessages({ customerName: "Please Refine your search" });
    }
  }, [isError]);

  useEffect(() => {
    return () => {
      dispatch(clearCustomerSearchData()); // Clear data on unmount
    };
  }, [dispatch]);

  useEffect(() => {
    setErrorMessages({});
  }, []);
  return (
    <>
      <Box sx={CustomerlistingStyles.container}>
        <Box sx={{ p: 2 }}>
          {/* Title with spacing */}
          <Typography
            variant="h5"
            sx={CustomerlistingStyles.customerSearchStyles}
          >
            {t("Customer Search")}
          </Typography>
          <Typography
            variant="body1"
            sx={CustomerlistingStyles.useOneOrMoreFieldsStyles}
          >
            {t("Use one or more fields to search for customers")}
          </Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={4}>
              <Box sx={{ height: "100%" }}>
                <TextField
                  fullWidth
                  label="Search by Name"
                  variant="outlined"
                  name="customerName"
                  value={searchParams.customerName}
                  onChange={handleChange}
                  id="agent-customer-name-search"
                  data-test-id="test-customer-name-search"
                  aria-label="Search customers by name"
                />
                {errorMessages.customerName && (
                  <Typography
                    color={
                      errorMessages.customerName.includes("Refine")
                        ? "black"
                        : "error"
                    }
                    sx={{ mt: 1 }}
                  >
                    {errorMessages.customerName}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ height: "100%" }}>
                <TextField
                  fullWidth
                  label="Search by Mobile Number"
                  variant="outlined"
                  name="mobileNumber"
                  value={searchParams.mobileNumber}
                  onChange={handleChange}
                  id="agent-mobile-number-search"
                  data-test-id="test-mobile-number-search"
                  aria-label="Search customers by mobile number"
                />
                {errorMessages.mobileNumber && (
                  <Typography
                    color={
                      errorMessages.mobileNumber.includes("Refine")
                        ? "black"
                        : "error"
                    }
                    sx={{ mt: 1 }}
                  >
                    {errorMessages.mobileNumber}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ height: "100%" }}>
                <TextField
                  fullWidth
                  label="Search by Zipcode"
                  variant="outlined"
                  name="postalCode"
                  value={searchParams.postalCode}
                  onChange={handleChange}
                  id="agent-postal-code-search"
                  data-test-id="test-postal-code-search"
                  aria-label="Search customers by zipcode"
                />
                {errorMessages.postalCode && (
                  <Typography
                    color={
                      errorMessages.postalCode.includes("Refine")
                        ? "black"
                        : "error"
                    }
                    sx={{ mt: 1 }}
                  >
                    {errorMessages.postalCode}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                id="agent-search-customers-button"
                data-test-id="test-search-customers-button"
                aria-label="Search customers"
              >
                Search Customers
              </Button>
            </Grid>
          </Grid>
        </Box>
        {!customerSerachData ? (
          isError ? (
            <></>
          ) : (
            ""
          )
        ) : (
          <Box sx={CustomerlistingStyles.headingContainer}>
            <HeadingText
              additionalHeadingClass={CustomerlistingStyles.heading}
              heading={isSmallScreen ? CUSTOMERS : CUSTOMER_LISTING_CAPS}
            />
            {/* <ImageAndText
              onhandleClick={handleClick}
              image={filterIcon}
              text={FILTER}
            /> */}
          </Box>
        )}
        {!customerSerachData ? (
          isError ? (
            <></>
          ) : (
            ""
          )
        ) : isSmallScreen ? (
          <CustomerListCards
            customerListingData={customerSerachData?.searchList ?? []}
          />
        ) : (
          <CustomerList
            customerListingData={customerSerachData?.searchList ?? []}
            headerData={customerListingTableHeadingsData}
          />
        )}
      </Box>
      {/* <FilterPopUp
        options={filterPopUpCommonOptions}
        selectedKey={selectedKey}
        onSelect={handleSelect}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      /> */}
    </>
  );
};

export default CustomerListing;
